import { normalizeDate } from './normalizeDate';

/**
 * Returns a relative date by adding the number of days to today.
 * @param dayDifference The number of days to add to today. It can be also negative for overdue dates.
 */
export function getRelativeDateFromToday(dayDifference: number): Date {
  const today = normalizeDate(new Date(Date.now()));
  return new Date(today.setDate(today.getDate() + dayDifference));
}

function compareDates(first: Date, second: Date): boolean {
  return first.getTime() === second.getTime();
}

/**
 * Compare the given date with a relative date from today computed based on the day difference.
 * For example dayDifference = -1 means yesterday.
 * @param date The date to check.
 * @param dayDifference The number of days from today.
 */
function compareWithRelativeDate(date: Date, dayDifference: number): boolean {
  const dateFromToday = getRelativeDateFromToday(dayDifference);
  const normalizedDate = normalizeDate(date);
  return compareDates(dateFromToday, normalizedDate);
}

/**
 * Check if the date is today.
 * @param date The date to check.
 */
export function isToday(date: Date): boolean {
  return compareWithRelativeDate(date, 0);
}

/**
 * Check if the date is yesterday.
 * @param date The date to check.
 */
export function isYesterday(date: Date): boolean {
  return compareWithRelativeDate(date, -1);
}

/**
 * Check if the date is tomorrow.
 * @param date The date to check.
 */
export function isTomorrow(date: Date): boolean {
  return compareWithRelativeDate(date, 1);
}

/**
 * Check if the date is within the last number of days.
 * @param date The date to check.
 * @param days The number of days for the interval.
 */
export function isWithinLastDays(date: Date, days: number): boolean {
  const daysAgo = getRelativeDateFromToday(-days);
  const normalizedDate = normalizeDate(date);
  return normalizedDate.getTime() >= daysAgo.getTime();
}

/**
 * Check if the date is within the current year.
 * @param date The date to check.
 */
export function isThisYear(date: Date): boolean {
  const now = new Date(Date.now());
  return now.getFullYear() === date.getFullYear();
}
